import * as plugins from '../../00plugins.js';
import * as interfaces from '../../interfaces/index.js';
import { zIndexLayers } from '../../00zindex.js';

import {
  DeesElement,
  type TemplateResult,
  property,
  customElement,
  html,
  css,
  cssManager,
} from '@design.estate/dees-element';
import { DeesContextmenu } from '../../00group-overlay/dees-contextmenu/dees-contextmenu.js';
import { demoFunc } from './dees-appui-mainmenu.demo.js';
import { themeDefaultStyles } from '../../00theme.js';

/**
 * the most left menu
 * usually used as organization selector
 */
@customElement('dees-appui-mainmenu')
export class DeesAppuiMainmenu extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['App UI'];

  // INSTANCE

  // Logo properties
  @property({ type: String })
  accessor logoIcon: string = '';

  @property({ type: String })
  accessor logoText: string = '';

  // Menu groups (new way)
  @property({ type: Array })
  accessor menuGroups: interfaces.IMenuGroup[] = [];

  // Bottom tabs (pinned to bottom)
  @property({ type: Array })
  accessor bottomTabs: interfaces.IMenuItem[] = [];

  // Legacy tabs property (for backward compatibility)
  @property({ type: Array })
  accessor tabs: interfaces.IMenuItem[] = [];

  @property()
  accessor selectedTab!: interfaces.IMenuItem;

  @property({ type: Boolean, reflect: true })
  accessor collapsed: boolean = false;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        --menu-width-expanded: 200px;
        --menu-width-collapsed: 56px;
        --tooltip-bg: var(--dees-color-tooltip-bg);
        --tooltip-fg: var(--dees-color-tooltip-fg);
        position: relative;
        display: block;
        height: 100%;
      }

      .mainContainer {
        color: var(--dees-color-text-secondary);
        z-index: ${zIndexLayers.fixed.appBar};
        display: flex;
        flex-direction: column;
        position: relative;
        width: var(--menu-width-expanded);
        height: 100%;
        background: var(--dees-color-bg-secondary);
        user-select: none;
        border-right: 1px solid var(--dees-color-border-subtle);
        font-family: var(--dees-font-family);
        transition: width var(--dees-transition-slow) var(--dees-ease-standard);
      }

      :host([collapsed]) .mainContainer {
        width: var(--menu-width-collapsed);
      }

      /* Floating collapse toggle button */
      .collapse-toggle {
        position: absolute;
        right: -12px;
        top: 24px;
        transform: translateY(-50%);
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: var(--dees-color-bg-primary);
        border: 1px solid var(--dees-color-border-strong);
        box-shadow: var(--dees-shadow-sm);
        cursor: pointer;
        z-index: 10;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--dees-color-text-muted);
        opacity: 0;
        transition: opacity var(--dees-transition-default) var(--dees-ease-standard), background var(--dees-transition-fast) var(--dees-ease-standard);
        padding: 0;
      }

      .collapse-toggle:hover {
        background: var(--dees-color-bg-tertiary);
        color: var(--dees-color-text-primary);
      }

      :host(:hover) .collapse-toggle {
        opacity: 1;
      }

      .collapse-toggle dees-icon {
        font-size: 14px;
      }

      /* Logo Section */
      .logoSection {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
        height: var(--dees-control-height-xl);
        padding: 0 14px;
        border-bottom: 1px solid var(--dees-color-border-subtle);
        flex-shrink: 0;
        box-sizing: border-box;
      }

      .logoSection .logoIcon {
        font-size: var(--dees-font-title2-size);
        color: var(--dees-color-text-primary);
        flex-shrink: 0;
      }

      .logoSection .logoText {
        flex: 1;
        font-size: var(--dees-font-subhead-size);
        font-weight: 600;
        color: var(--dees-color-text-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        transition: opacity var(--dees-transition-default) var(--dees-ease-standard), width var(--dees-transition-slow) var(--dees-ease-standard);
      }

      :host([collapsed]) .logoSection {
        justify-content: center;
        padding: 0;
        gap: 0;
      }

      :host([collapsed]) .logoSection .logoText {
        display: none;
      }

      /* Middle Section (scrollable) */
      .menuSection {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        overscroll-behavior: contain;
        padding: var(--dees-spacing-sm) 0;
      }

      .menuSection::-webkit-scrollbar {
        width: 6px;
      }

      .menuSection::-webkit-scrollbar-track {
        background: transparent;
      }

      .menuSection::-webkit-scrollbar-thumb {
        background: var(--dees-color-scrollbar-thumb);
        border-radius: var(--dees-radius-xs);
      }

      .menuSection::-webkit-scrollbar-thumb:hover {
        background: var(--dees-color-scrollbar-thumb-hover);
      }

      /* Menu Group */
      .menuGroup {
        padding: 0 var(--dees-spacing-sm);
        margin-bottom: var(--dees-spacing-sm);
      }

      .menuGroup:last-child {
        margin-bottom: 0;
      }

      .groupHeader {
        padding: 8px 12px 6px;
        font-size: var(--dees-font-caption2-size);
        font-weight: 600;
        color: var(--dees-color-text-muted);
        text-transform: uppercase;
        letter-spacing: 0.5px;
        white-space: nowrap;
        overflow: hidden;
        transition: opacity var(--dees-transition-default) var(--dees-ease-standard), max-height var(--dees-transition-slow) var(--dees-ease-standard);
        max-height: 30px;
      }

      :host([collapsed]) .groupHeader {
        opacity: 0;
        max-height: 0;
        padding: 0;
        margin: 0;
      }

      .groupTabs {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      :host([collapsed]) .menuGroup {
        padding: 0 4px;
      }

      /* Tab Item */
      .tab {
        position: relative;
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-md);
        padding: var(--dees-spacing-sm) var(--dees-spacing-md);
        font-size: var(--dees-font-control-size-sm);
        font-weight: var(--dees-font-control-weight);
        border-radius: var(--dees-radius-md);
        cursor: pointer;
        transition: all var(--dees-transition-fast) var(--dees-ease-standard);
        color: var(--dees-color-text-secondary);
      }

      .tab:hover {
        background: var(--dees-color-hover);
        color: var(--dees-color-text-primary);
      }

      .tab:active {
        background: var(--dees-color-active);
      }

      /* Sidebar selection: a systemBlue-tinted filled rounded rect with
         systemBlue label/icon (no left-edge bar). */
      .tab.selectedTab {
        background: color-mix(in srgb, var(--dees-color-accent-primary) 14%, transparent);
        color: var(--dees-color-accent-primary);
      }

      .tab dees-icon {
        font-size: 16px;
        opacity: 0.85;
        flex-shrink: 0;
      }

      .tab.selectedTab dees-icon {
        opacity: 1;
      }

      .tab .tabLabel {
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        transition: opacity 0.2s ease, width 0.25s ease;
      }

      /* Collapsed tab styles */
      :host([collapsed]) .tab {
        justify-content: center;
        padding: var(--dees-spacing-sm);
        gap: 0;
      }

      :host([collapsed]) .tab .tabLabel {
        opacity: 0;
        width: 0;
        position: absolute;
      }

      /* Tooltip for collapsed state */
      .tab-tooltip {
        position: absolute;
        left: 100%;
        top: 50%;
        transform: translateY(-50%);
        margin-left: var(--dees-spacing-md);
        padding: 6px var(--dees-spacing-md);
        background: var(--tooltip-bg);
        color: var(--tooltip-fg);
        border-radius: var(--dees-radius-md);
        font-size: var(--dees-font-control-size-sm);
        font-weight: var(--dees-font-control-weight);
        white-space: nowrap;
        opacity: 0;
        pointer-events: none;
        transition: opacity var(--dees-transition-fast) var(--dees-ease-standard);
        z-index: 1000;
        box-shadow: var(--dees-shadow-md);
      }

      .tab-tooltip::before {
        content: '';
        position: absolute;
        left: -4px;
        top: 50%;
        transform: translateY(-50%);
        border: 4px solid transparent;
        border-right-color: var(--tooltip-bg);
      }

      :host([collapsed]) .tab:hover .tab-tooltip {
        opacity: 1;
        transition-delay: 1s;
      }

      /* Badge styles */
      .badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 18px;
        height: 18px;
        padding: 0 6px;
        font-size: var(--dees-font-caption2-size);
        font-weight: 600;
        border-radius: var(--dees-radius-full);
        margin-left: auto;
      }

      .badge.default {
        background: var(--dees-color-badge-default-bg);
        color: var(--dees-color-badge-default-fg);
      }

      .badge.success {
        background: var(--dees-color-badge-success-bg);
        color: var(--dees-color-badge-success-fg);
      }

      .badge.warning {
        background: var(--dees-color-badge-warning-bg);
        color: var(--dees-color-badge-warning-fg);
      }

      .badge.error {
        background: var(--dees-color-badge-error-bg);
        color: var(--dees-color-badge-error-fg);
      }

      :host([collapsed]) .badge {
        display: none;
      }

      /* Bottom Section */
      .bottomSection {
        flex-shrink: 0;
        padding: var(--dees-spacing-sm);
        border-top: 1px solid var(--dees-color-border-subtle);
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      :host([collapsed]) .bottomSection {
        padding: 8px 4px;
      }
    `,
  ];

  public render(): TemplateResult {
    // Get all tabs for selection (from groups or legacy tabs)
    const allTabs = this.getAllTabs();

    return html`
      <div class="mainContainer" @contextmenu=${(eventArg: MouseEvent) => {
        DeesContextmenu.openContextMenuWithOptions(eventArg, [{
          name: 'app settings',
          action: async () => {},
          iconName: 'lucide:settings',
        }])
      }}>
        ${this.logoIcon || this.logoText ? html`
          <div class="logoSection">
            ${this.logoIcon ? html`<dees-icon class="logoIcon" .icon="${this.logoIcon}"></dees-icon>` : ''}
            ${this.logoText ? html`<span class="logoText">${this.logoText}</span>` : ''}
          </div>
        ` : ''}

        <div class="menuSection">
          ${this.menuGroups.length > 0 ? this.renderMenuGroups() : this.renderLegacyTabs()}
        </div>

        ${this.bottomTabs.length > 0 ? html`
          <div class="bottomSection">
            ${this.bottomTabs.map((tabArg) => this.renderTab(tabArg))}
          </div>
        ` : ''}
      </div>
      <button class="collapse-toggle" @click="${() => this.toggleCollapse()}">
        <dees-icon .icon="${this.collapsed ? 'lucide:chevronRight' : 'lucide:chevronLeft'}"></dees-icon>
      </button>
    `;
  }

  private renderMenuGroups(): TemplateResult {
    return html`
      ${this.menuGroups.map((group) => html`
        <div class="menuGroup">
          ${group.name ? html`<div class="groupHeader">${group.name}</div>` : ''}
          <div class="groupTabs">
            ${group.items.map((tabArg) => this.renderTab(tabArg))}
          </div>
        </div>
      `)}
    `;
  }

  private renderLegacyTabs(): TemplateResult {
    return html`
      <div class="menuGroup">
        <div class="groupTabs">
          ${this.tabs.map((tabArg) => this.renderTab(tabArg))}
        </div>
      </div>
    `;
  }

  private renderTab(tabArg: interfaces.IMenuItem): TemplateResult {
    return html`
      <div
        class="tab ${tabArg === this.selectedTab ? 'selectedTab' : ''}"
        @click="${() => {
          this.updateTab(tabArg);
        }}"
      >
        <dees-icon .icon="${tabArg.iconName || ''}"></dees-icon>
        <span class="tabLabel">${tabArg.key}</span>
        ${tabArg.badge !== undefined ? html`
          <span class="badge ${tabArg.badgeVariant || 'default'}">${tabArg.badge}</span>
        ` : ''}
        <span class="tab-tooltip">${tabArg.key}</span>
      </div>
    `;
  }

  private getAllTabs(): interfaces.IMenuItem[] {
    if (this.menuGroups.length > 0) {
      const groupTabs = this.menuGroups.flatMap(group => group.items);
      return [...groupTabs, ...this.bottomTabs];
    }
    return [...this.tabs, ...this.bottomTabs];
  }

  updateTab(tabArg: interfaces.IMenuItem) {
    this.selectedTab = tabArg;
    this.selectedTab.action();

    // Emit tab-select event
    this.dispatchEvent(new CustomEvent('tab-select', {
      detail: { tab: tabArg },
      bubbles: true,
      composed: true
    }));
  }

  firstUpdated() {
    const allTabs = this.getAllTabs();
    if (allTabs.length > 0) {
      this.updateTab(allTabs[0]);
    }
  }

  public toggleCollapse(): void {
    this.collapsed = !this.collapsed;
    this.dispatchEvent(new CustomEvent('collapse-change', {
      detail: { collapsed: this.collapsed },
      bubbles: true,
      composed: true
    }));
  }
}
